import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { userIsBuero, userIsRealAppAdmin, type CurrentUser } from "@/lib/auth";
import {
  listMaterialAnforderungen,
  type MaListItem,
  type MaPositionPreview,
} from "@/lib/material";
import type { Order } from "@/lib/projects";
import { useRealtimeSync } from "@/lib/useRealtime";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { MaterialRequestQrDialog } from "@/projectOverview/MaterialRequestQrDialog";
import { MaDialog } from "@/pages/material/MaDialog";
import {
  fmtMaDateTime,
  MA_FORTSCHRITT_PILL,
  maBearbeiterDisplayName,
  maFortschrittLabel,
  maLieferterminSummary,
  normalizeMonteurFortschritt,
} from "@/pages/material/maListDisplay";

export function MaterialRequestsSection({
  project,
  canCreate,
  canCreateQr,
  user,
  onChanged,
}: {
  project: Order;
  canCreate: boolean;
  canCreateQr: boolean;
  user: CurrentUser | null;
  onChanged: () => void;
}) {
  const [editor, setEditor] = useState<{ open: boolean; maId: number | null }>({
    open: false,
    maId: null,
  });
  const [qrOpen, setQrOpen] = useState(false);
  const [items, setItems] = useState<MaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);
  const canProcess = userIsBuero(user) || userIsRealAppAdmin(user);

  const load = useCallback(async (opts?: { reset?: boolean }) => {
    const gen = ++requestRef.current;
    if (opts?.reset) {
      setItems([]);
      setError(null);
      setLoading(true);
    }
    try {
      const list = await listMaterialAnforderungen({ auftragId: project.id });
      if (gen !== requestRef.current) return;
      const rows = (Array.isArray(list) ? list : []).filter(
        (ma) => Number(ma.auftrag_id) === Number(project.id),
      );
      setItems(rows);
      setError(null);
    } catch (err) {
      if (gen !== requestRef.current) return;
      setError(err instanceof Error ? err.message : "Materialanforderungen konnten nicht geladen werden.");
    } finally {
      if (gen === requestRef.current) setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    void load({ reset: true });
  }, [load]);

  useEffect(() => {
    function onLocalChanged() {
      void load();
    }
    window.addEventListener("material_anforderungen_changed", onLocalChanged);
    window.addEventListener("material_anforderung_changed", onLocalChanged);
    return () => {
      window.removeEventListener("material_anforderungen_changed", onLocalChanged);
      window.removeEventListener("material_anforderung_changed", onLocalChanged);
    };
  }, [load]);

  useRealtimeSync({
    domains: ["material_anforderungen"],
    extraEvents: ["material_anforderung_changed", "material-anforderungen_changed"],
    paused: editor.open,
    onChange: () => {
      void load();
    },
  });

  if (!canCreate && !canCreateQr) return null;

  function handleChanged() {
    void load();
    onChanged();
  }

  return (
    <>
      <OverviewCard title="Materialanforderungen">
        {canCreate || canCreateQr ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {canCreate ? (
              <Button type="button" size="sm" onClick={() => setEditor({ open: true, maId: null })}>
                Neu
              </Button>
            ) : null}
            {canCreateQr ? (
              <Button
                type="button"
                size="sm"
                variant={canCreate ? "secondary" : "primary"}
                onClick={() => setQrOpen(true)}
              >
                QR-Code
              </Button>
            ) : null}
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner />
            <span>Lädt …</span>
          </div>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {!loading && !error && items.length === 0 ? (
          <p className="text-sm text-label-secondary">
            {canCreate
              ? "Neue Anforderung mit diesem Projekt vorausgewählt."
              : "QR-Code oder Link für die Baustelle. Nach dem Scannen öffnet sich eine neue Materialanforderung zu diesem Projekt."}
          </p>
        ) : null}

        {items.length > 0 ? (
          <div className="eh-po-ma-list">
            {items.map((ma) => (
              <MaOverviewRow key={ma.id} ma={ma} onOpen={() => setEditor({ open: true, maId: ma.id })} />
            ))}
          </div>
        ) : null}
      </OverviewCard>
      <MaDialog
        open={editor.open}
        onClose={() => setEditor((prev) => ({ ...prev, open: false }))}
        onChanged={handleChanged}
        maId={editor.maId}
        canProcess={canProcess}
        initialAuftragId={project.id}
      />
      {canCreateQr ? (
        <MaterialRequestQrDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          orderId={project.id}
        />
      ) : null}
    </>
  );
}

function MaOverviewRow({ ma, onOpen }: { ma: MaListItem; onOpen: () => void }) {
  const headline = maHeadline(ma);
  const preview = Array.isArray(ma.position_preview) ? ma.position_preview : [];
  const liefer = maLieferterminSummary(ma);
  const bearbeiter = maBearbeiterDisplayName(ma);
  const fortschritt = normalizeMonteurFortschritt(ma.monteur_fortschritt, ma.status);
  const closed = fortschritt === "abgelehnt" || fortschritt === "geliefert";
  const orders = linkedOrders(ma);

  return (
    <button
      type="button"
      className={clsx("eh-po-order-card eh-po-ma-card", closed && "eh-po-ma-card--closed")}
      onClick={onOpen}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs font-semibold text-accent">{ma.ma_nummer}</span>
        <span
          className={clsx(
            "inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold",
            MA_FORTSCHRITT_PILL[fortschritt],
          )}
        >
          {maFortschrittLabel(fortschritt)}
        </span>
      </div>
      {headline ? <p className="mt-1 text-sm font-medium leading-snug text-label">{headline}</p> : null}
      {preview.length > 0 ? (
        <ul className="eh-po-ma-positions">
          {preview.slice(0, 3).map((pos, index) => (
            <li key={`${ma.id}-${index}`} className="eh-po-ma-position">
              <PositionLine pos={pos} />
            </li>
          ))}
          {(ma.position_count || 0) > preview.slice(0, 3).length ? (
            <li className="eh-po-ma-position">
              + {(ma.position_count || 0) - Math.min(preview.length, 3)} weitere
            </li>
          ) : null}
        </ul>
      ) : (ma.position_count || 0) > 0 ? (
        <p className="eh-po-ma-meta">{ma.position_count} Positionen</p>
      ) : null}
      <div className="eh-po-ma-meta">
        <div>
          Erstellt {fmtMaDateTime(ma.created_at)}
          {ma.created_by ? ` · ${ma.created_by}` : ""}
          {(ma.attachment_count || 0) > 0
            ? ` · ${ma.attachment_count} ${ma.attachment_count === 1 ? "Anhang" : "Anhänge"}`
            : ""}
        </div>
        {liefer ? <div>Lieferung {liefer}</div> : null}
        {bearbeiter ? <div>Bearbeiter {bearbeiter}</div> : null}
      </div>
      {orders.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {orders.map((order, index) => (
            <span
              key={`${order.label}-${index}`}
              className={clsx(
                "eh-order-tk-badge font-mono text-xs font-semibold",
                order.cancelled && "eh-order-tk-badge--cancelled",
              )}
              title={order.cancelled ? `${order.label} (storniert)` : order.label}
            >
              {order.label}
            </span>
          ))}
        </div>
      ) : null}
    </button>
  );
}

function PositionLine({ pos }: { pos: MaPositionPreview }) {
  const qty = pos.menge != null ? String(pos.menge) : "";
  const unit = (pos.einheit || "").trim();
  const text = (pos.text || "").trim() || "—";
  return (
    <>
      {qty ? <span className="tabular-nums font-medium text-label">{qty}</span> : null}
      {unit ? <span className="mx-1">{unit}</span> : qty ? <span className="mx-1" /> : null}
      <span>{text}</span>
    </>
  );
}

function maHeadline(ma: MaListItem): string {
  const title = (ma.title || "").trim();
  if (title) return title;
  const notes = (ma.notes || "").trim();
  if (!notes) return "";
  return notes.length > 140 ? `${notes.slice(0, 137)}…` : notes;
}

function linkedOrders(ma: MaListItem): { label: string; cancelled: boolean }[] {
  const fromTk = (ma.linked_tk || [])
    .map((tk) => ({
      label: (tk.bestellnummer || "").trim(),
      cancelled: Boolean(tk.cancelled_at),
    }))
    .filter((tk) => tk.label);
  if (fromTk.length) return fromTk;
  return (ma.linked_bestellnummern || [])
    .map((bn) => ({ label: String(bn).trim(), cancelled: false }))
    .filter((tk) => tk.label);
}
